import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { Calendar } from '@vendasta/meetings';
import { Observable } from 'rxjs';
import { combineLatestWith, switchMap, take, tap } from 'rxjs/operators';
import { BookMeNowStoreService } from '../../book-me-now-store.service';
import { TimeSlot } from '../../bookme/bookme';

import { ActivatedRoute, Router } from '@angular/router';

@Component({
  selector: 'app-bookme-reschedule-confirmation',
  templateUrl: './confirmation.component.html',
  styleUrls: ['./confirmation.component.scss'],
  standalone: false,
})
export class BookmeRescheduleConfirmationComponent implements OnInit {
  @Output() cancelled = new EventEmitter<null>();

  calendar$: Observable<Calendar>;
  timeSlot$: Observable<TimeSlot>;
  timezoneOffset$: Observable<string>;
  displayVideoLink$: boolean;
  location$: Observable<string>;
  isTimeSlotChanged$: boolean;
  isLocationChanged$: boolean;

  constructor(
    private readonly bookmenowService: BookMeNowStoreService,
    private readonly router: Router,
    private readonly route: ActivatedRoute
  ) {}

  ngOnInit(): void {
    // Check if we have the necessary state data
    // If not (e.g., page refresh), redirect back to reschedule page to reload
    const currentState = this.bookmenowService.getCurrentState();
    if (!currentState.selectedTimeSlot) {
      // No time slot data means state was lost (page refresh)
      // Navigate back to the reschedule page to reload the meeting info
      this.router.navigate(['../'], {
        relativeTo: this.route,
        queryParamsHandling: 'preserve',
      });
      return;
    }

    this.calendar$ = this.bookmenowService.calendar$;
    this.timeSlot$ = this.bookmenowService.timeSlot$;
    this.timezoneOffset$ = this.bookmenowService.timezoneOffset$;
    this.displayVideoLink$ = this.bookmenowService.isVideoMeetingDisabled();
    this.location$ = this.bookmenowService.meetingLocation$;
    this.isLocationChanged$ = this.bookmenowService.getLocationChanged();
    this.isTimeSlotChanged$ = this.bookmenowService.getTimeSlotChanged();
  }

  onConfirm(): void {
    this.location$
      .pipe(
        combineLatestWith(this.timeSlot$),
        take(1),
        switchMap(([location, timeSlot]) => {
          const meetingLocation = this.isLocationChanged$
            ? location
            : undefined;
          const startTime = this.isTimeSlotChanged$
            ? timeSlot.start
            : undefined;

          return this.bookmenowService
            .rescheduleMeeting({ start: startTime, location: meetingLocation })
            .pipe(
              tap(() => {
                // Navigate to confirmation with only action query param (meetingId is in route)
                const confirmationParams = {
                  action: 'reschedule',
                };

                this.router.navigate(['../confirmation'], {
                  relativeTo: this.route,
                  queryParams: confirmationParams,
                  queryParamsHandling: 'merge',
                });
              }),
              tap({
                error: (err) => {
                  console.error(err);
                  this.bookmenowService.handleMeetingError(err, 'reschedule');
                },
              })
            );
        })
      )
      .subscribe();
  }

  onCancel(): void {
    this.bookmenowService.setIsTimeSlotChanged(false);
    this.router.navigate(['../'], {
      relativeTo: this.route,
      queryParamsHandling: 'preserve',
    });
  }
}
